/**
 * Guest-facing gift card calls (spec 2026-10-08 §4) — the public balance check
 * and, in slice 2b, checkout holds. Through the canonical `createApiClient`, so
 * every call carries the App Check attestation the routes enforce.
 *
 * Refusals come back as the API's `{ error: { code, message, details } }`;
 * {@link readGiftCardPublicError} turns a thrown axios error into one of the
 * codes below so a component never parses messages.
 */
import { createApiClient } from './apiClient';

const publicGiftCardsApi = createApiClient('/api/v1/public/gift-cards');

export interface PublicGiftCardBalance {
    balanceCents: number;
    /** `none` covers every card that cannot be spent here — and no card at all. */
    status: 'active' | 'none';
    last4: string;
}

/** Fields every guarded public gift card request carries. */
export interface GiftCardProbeFields {
    number: string;
    sessionId: string;
    deviceId?: string;
    turnstileToken?: string;
    /** The card's PIN, where the location asks for one (see {@link GiftCardPinMode}). */
    pin?: string;
}

/**
 * How a page shows the PIN field (spec 2026-10-08 §13): `off` — none; `card` —
 * optional, "PIN, if your card has one"; `all` — required.
 */
export type GiftCardPinMode = 'off' | 'card' | 'all';

export type GiftCardPublicErrorCode =
    | 'GIFT_CARDS_ONLINE_UNAVAILABLE'
    | 'GIFT_CARD_NUMBER_INVALID'
    | 'GIFT_CARD_PIN_REQUIRED'
    | 'GIFT_CARD_PIN_INVALID'
    | 'TURNSTILE_REQUIRED'
    | 'GIFT_CARD_LOOKUP_BLOCKED'
    | 'GIFT_CARDS_ONLINE_TEMPORARILY_UNAVAILABLE'
    | 'UNKNOWN';

export interface GiftCardPublicError {
    code: GiftCardPublicErrorCode | string;
    message: string;
    /** Present with TURNSTILE_REQUIRED: the widget's public site key. */
    siteKey?: string | null;
}

export function readGiftCardPublicError(err: unknown): GiftCardPublicError {
    const body = (err as { response?: { data?: { error?: { code?: string; message?: string; details?: { siteKey?: string | null } } } } })
        ?.response?.data?.error;
    if (body?.code) {
        return {
            code: body.code,
            message: body.message ?? 'Something went wrong. Please try again.',
            ...(body.code === 'TURNSTILE_REQUIRED' ? { siteKey: body.details?.siteKey ?? null } : {}),
        };
    }
    return { code: 'UNKNOWN', message: 'Something went wrong. Please try again.' };
}

export async function checkPublicGiftCardBalance(
    slug: string,
    fields: GiftCardProbeFields,
): Promise<PublicGiftCardBalance> {
    const res = await publicGiftCardsApi.post<PublicGiftCardBalance>('/balance', { slug, ...fields });
    return res.data;
}

// ─── Checkout holds (slice 2b) ────────────────────────────────────────────────

export interface GiftCardHoldResponse {
    status: 'held' | 'none';
    holdId: string | null;
    amountCents: number;
    last4: string;
    expiresAt?: string;
}

/** Apply a card to the guest's cart: reserve min(available, maxAmountCents). */
export async function placeGiftCardHold(
    cartId: number,
    maxAmountCents: number,
    fields: GiftCardProbeFields,
): Promise<GiftCardHoldResponse> {
    const res = await publicGiftCardsApi.post<GiftCardHoldResponse>('/hold', { cartId, maxAmountCents, ...fields });
    return res.data;
}

/** Re-size the guest's hold to a new total (no number needed). */
export async function resizeGiftCardHold(
    cartId: number,
    holdId: string,
    maxAmountCents: number,
): Promise<GiftCardHoldResponse> {
    const res = await publicGiftCardsApi.post<GiftCardHoldResponse>('/hold', { cartId, holdId, maxAmountCents });
    return res.data;
}

export async function releaseGiftCardHold(cartId: number, holdId: string): Promise<void> {
    await publicGiftCardsApi.post('/hold/release', { cartId, holdId });
}

/**
 * Is the gift card online surface live at this location? A 404 (dark, no
 * program, unknown slug) reads `false`; any other failure reads `false` too —
 * the checkout then simply offers no gift card, never a broken one.
 */
export async function isGiftCardOnlineAvailable(slug: string): Promise<boolean> {
    return (await getGiftCardOnlineAvailability(slug)).available;
}

/** Availability plus how to show the PIN field. Any failure reads unavailable. */
export async function getGiftCardOnlineAvailability(
    slug: string,
): Promise<{ available: boolean; pin: GiftCardPinMode }> {
    try {
        const res = await publicGiftCardsApi.get<{ available: boolean; pin?: GiftCardPinMode }>(
            `/${encodeURIComponent(slug)}/availability`,
        );
        const pin = res.data?.pin === 'card' || res.data?.pin === 'all' ? res.data.pin : 'off';
        return { available: res.data?.available === true, pin };
    } catch {
        return { available: false, pin: 'off' };
    }
}
